import { useEffect, useRef } from 'react';

/**
 * Run a callback when the tab becomes hidden / visible again.
 *
 * Browsers throttle and eventually freeze timers in a background tab, so a poller that only
 * relies on setInterval comes back stale: the user returns to a screen showing data from
 * before they left, and waits a full interval before it refreshes. That is what reads as
 * "I have to reload the page manually".
 *
 * The callbacks are held in refs so the listener always calls the latest render's closure
 * without re-subscribing on every render.
 */
export default function usePageVisibility(onVisible, onHidden) {
    const onVisibleRef = useRef(onVisible);
    const onHiddenRef = useRef(onHidden);

    onVisibleRef.current = onVisible;
    onHiddenRef.current = onHidden;

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                onHiddenRef.current && onHiddenRef.current();
            } else {
                onVisibleRef.current && onVisibleRef.current();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
}
